import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../../../../../api/firebase/firestoreService";
import { CmmInputUniqueValuesTableDataModel } from "../../../../../../../data-model/tablesData";
import { ModalId } from "../../../../../../../enums/modalId";
import { CmmInputUniqueValuesTableColumnKeys } from "../../../../../../../enums/tableColumnKeys";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../../../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../../../../../../redux/store/store";
import {
  normalizeTopValues,
  type CmmInputUniqueValueItem,
} from "../cmmInputUniqueValuesTypes";
import { useCmmInputUniqueValues } from "./useCmmInputUniqueValues";
import { useTableProfile } from "./useTableProfile";

export const useConditionList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId, metaData } = useSelector(
    (state: RootState) => state.modalSliceNew,
  );
  const {
    cmmInputUniqueValues,
    cmmInputUniqueValuesTableData,
    loading,
    error,
    refetch,
    saveTopValues,
  } = useCmmInputUniqueValues();
  const { refreshing, refreshTableProfile } = useTableProfile();

  const handleSaveTopValues = useCallback(
    async (columnName: string, newTopValues: CmmInputUniqueValueItem[]) => {
      try {
        await saveTopValues(columnName, newTopValues);
        toast.success("Top values saved successfully.");
      } catch {
        toast.error("Failed to save top values. Please try again.");
      }
    },
    [saveTopValues],
  );

  useEffect(() => {
    if (openedModalId === ModalId.CONDITION_LIST_TOP_VALUES_MODAL) {
      openModal(ModalId.CONDITION_LIST_TOP_VALUES_MODAL);
    }
  }, [openedModalId]);

  const handleEditIconClick = useCallback(
    (rowData: Record<string, unknown>) => {
      const columnName = rowData[
        CmmInputUniqueValuesTableColumnKeys.COLUMN_NAME
      ] as string;
      const column = cmmInputUniqueValues?.columns?.find(
        (c) => c.column_name === columnName,
      );
      if (!column) return;
      dispatch(
        setOpenedModalId({
          id: ModalId.CONDITION_LIST_TOP_VALUES_MODAL,
          metaData: {
            columnName: column.column_name,
            dataType: column.data_type,
            topValues: normalizeTopValues(column.top_values),
          },
        }),
      );
    },
    [cmmInputUniqueValues, dispatch],
  );

  const handleCloseModal = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const handleRefresh = useCallback(async () => {
    await refreshTableProfile();
    await refetch();
  }, [refreshTableProfile, refetch]);

  const SHOW_ON_LIST_PREFIX = "cmm-show-on-list-";

  const handleShowOnListChange = useCallback(
    (
      data: { name: string; required: boolean; value: boolean },
      setWorkingTableData: React.Dispatch<
        React.SetStateAction<CmmInputUniqueValuesTableDataModel[]>
      >,
      setIsDirty: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      if (
        typeof data.name !== "string" ||
        !data.name.startsWith(SHOW_ON_LIST_PREFIX)
      )
        return;
      const columnName = data.name.slice(SHOW_ON_LIST_PREFIX.length);
      setWorkingTableData((prev) =>
        prev.map((row) => {
          const rowColumnName = row[
            CmmInputUniqueValuesTableColumnKeys.COLUMN_NAME
          ] as string;
          if (rowColumnName !== columnName) return row;
          const currentSwitch =
            row[CmmInputUniqueValuesTableColumnKeys.SHOW_ON_LIST];
          return {
            ...row,
            [CmmInputUniqueValuesTableColumnKeys.SHOW_ON_LIST]: {
              ...currentSwitch,
              defaultChecked: data.value,
            },
          };
        }),
      );
      setIsDirty(true);
    },
    [],
  );

  const handleFieldNameChange = useCallback(
    (
      rowIndex: number,
      fieldKey: string,
      newValue: unknown,
      setWorkingTableData: React.Dispatch<
        React.SetStateAction<CmmInputUniqueValuesTableDataModel[]>
      >,
      setIsDirty: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      if (fieldKey !== CmmInputUniqueValuesTableColumnKeys.FIELD_NAME) return;
      setWorkingTableData((prev) =>
        prev.map((row, idx) =>
          idx === rowIndex
            ? {
                ...row,
                [CmmInputUniqueValuesTableColumnKeys.FIELD_NAME]: {
                  data:
                    typeof newValue === "string"
                      ? newValue
                      : String(newValue ?? ""),
                },
              }
            : row,
        ),
      );
      setIsDirty(true);
    },
    [],
  );

  const handleSave = useCallback(
    async (
      workingTableData: CmmInputUniqueValuesTableDataModel[],
      setSaving: React.Dispatch<React.SetStateAction<boolean>>,
      setIsDirty: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      if (!cmmInputUniqueValues?.columns?.length) return;
      setSaving(true);
      try {
        const rowLookup = new Map<
          string,
          { showOnList: boolean; fieldName: string }
        >();
        workingTableData.forEach((row) => {
          const name = row[
            CmmInputUniqueValuesTableColumnKeys.COLUMN_NAME
          ] as string;
          const showCell =
            row[CmmInputUniqueValuesTableColumnKeys.SHOW_ON_LIST];
          const fieldCell = row[CmmInputUniqueValuesTableColumnKeys.FIELD_NAME];
          rowLookup.set(name, {
            showOnList: showCell?.defaultChecked ?? false,
            fieldName: fieldCell?.data ?? "",
          });
        });
        const updatedColumns = cmmInputUniqueValues.columns.map((col) => {
          const lookup = rowLookup.get(col.column_name);
          const displayName = lookup?.fieldName ?? col.display_name ?? "";
          return {
            ...col,
            show_on_condition_list:
              lookup?.showOnList ?? col.show_on_condition_list,
            display_name: displayName,
          };
        });
        await FirestoreService.updateDocument(
          "flagging_algorithm_config",
          "cmm_input_unique_values",
          { columns: updatedColumns },
        );
        await refetch();
        setIsDirty(false);
        toast.success("Condition list saved successfully.");
      } catch {
        toast.error("Failed to save. Please try again.");
      } finally {
        setSaving(false);
      }
    },
    [cmmInputUniqueValues, refetch],
  );

  return {
    cmmInputUniqueValuesTableData,
    loading,
    error,
    refreshing,
    openedModalId,
    metaData,
    handleSaveTopValues,
    handleEditIconClick,
    handleCloseModal,
    handleRefresh,
    handleShowOnListChange,
    handleFieldNameChange,
    handleSave,
  };
};
